/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './YarnTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { mainColors } from '../../helpers/colors';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CFormInput,
  CRow,
  CCol,
  CCollapse
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash, cilFilter, cilX, cilSearch } from '@coreui/icons';
import { deleteYarnRequestAction } from '../../store/yarn/yarnActions';

function YarnTable(props) {
  const {
    yarnList,
    yarnData,
    deleteYarnRequest,
    query,
    onFilterChange,
    onSortChange,
    onPageChange,
    onPageSizeChange
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(
    ['index', 'YarnCode', 'YarnName', 'YarnType', 'YarnFactor', 'YarnDescription', 'actions'],
    'YarnTable'
  );

  const [tableData, setTableData] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    YarnCode: '',
    YarnName: '',
    YarnFactor: ''
  });

  const columns = [
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'YarnCode', label: `${t('yarnCode')}`, sortable: true, filterable: true },
    { id: 'YarnName', label: t('yarnName'), sortable: true, filterable: true },
    { id: 'YarnType', label: t('yarnType'), sortable: true, filterable: false },
    { id: 'YarnFactor', label: t('yarnFactor'), sortable: true, filterable: true },
    { id: 'YarnDescription', label: t('Yarn Properties'), sortable: true, filterable: false },
    { id: 'actions', label: t('Actions'), sortable: false, filterable: false }
  ];

  useEffect(() => {
    if (yarnList && Array.isArray(yarnList)) {
      setTableData(yarnList);
    }
  }, [yarnList]);

  useEffect(() => {
    const parsedFactor = query?.YarnFactor !== null && query?.YarnFactor !== undefined
      ? (Number(query.YarnFactor) / 100).toFixed(2)
      : '';

    setFilters({
      YarnCode: query?.YarnCode || '',
      YarnName: query?.YarnName || '',
      YarnFactor: parsedFactor
    });
  }, [query?.YarnCode, query?.YarnName, query?.YarnFactor]);

  const getYarnTypeLabel = (type) => {
    switch (type) {
      case '1': return t('yarnTypeNe');
      case '2': return t('yarnTypeNm');
      case '3': return t('yarnTypeDny');
      default: return type;
    }
  };

  const normalizeFactorToInt = (rawValue) => {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const normalized = raw.replace(',', '.');
    if (!normalized.includes('.')) {
      const onlyDigits = normalized.replace(/\D/g, '');
      return onlyDigits ? parseInt(onlyDigits, 10) : null;
    }

    const [wholeRaw, decimalRaw = ''] = normalized.split('.');
    const whole = wholeRaw.replace(/\D/g, '');
    const decimal = decimalRaw.replace(/\D/g, '').slice(0, 2);

    const wholePart = whole ? parseInt(whole, 10) : 0;
    const decimalPart = decimal ? parseInt(decimal.padEnd(2, '0'), 10) : 0;
    return (wholePart * 100) + decimalPart;
  };

  const triggerFilter = (nextFilters) => {
    onFilterChange?.({
      YarnCode: nextFilters.YarnCode.trim() || null,
      YarnName: nextFilters.YarnName.trim() || null,
      YarnFactor: normalizeFactorToInt(nextFilters.YarnFactor)
    });
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = {
      ...filters,
      [key]: value
    };

    setFilters(nextFilters);
    triggerFilter(nextFilters);
  };

  const clearFilters = () => {
    const resetFilters = {
      YarnCode: '',
      YarnName: '',
      YarnFactor: ''
    };

    setFilters(resetFilters);
    triggerFilter(resetFilters);
  };

  const clearSingleFilter = (key) => {
    handleFilterChange(key, '');
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleEdit = (item) => {
    navigate(`/yarn/edit/${item.YarnId}`, {
      state: { yarn: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          YarnId: deleteModal.item.YarnId
        };

        deleteYarnRequest({ data: deleteData });
      }
    }

    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const scrollToTableContainer = () => {
    const tableContainer = document.querySelector('.yarn-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleChangePage = (newPage) => {
    onPageChange?.(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    onPageSizeChange?.(value);
    scrollToTableContainer();
  };

  const getOrderByValue = (columnId) => {
    const orderByMap = {
      YarnCode: 'yarncode',
      YarnName: 'yarnname',
      YarnFactor: 'yarnfactor',
      YarnDescription: 'yarndescription'
    };

    return orderByMap[columnId] || null;
  };

  const handleRequestSort = (property) => {
    const backendOrderBy = getOrderByValue(property);
    if (!backendOrderBy || !onSortChange) return;

    const currentOrderBy = query?.OrderBy || null;
    const currentDescending = query?.IsDescending !== false;
    const nextDescending = currentOrderBy === backendOrderBy ? !currentDescending : true;

    onSortChange(backendOrderBy, nextDescending);
  };

  const totalRows = yarnData?.TotalCount ?? tableData.length;
  const page = query?.PageNumber || 1;
  const rowsPerPage = query?.PageSize || 10;
  const totalPages = Math.max(1, yarnData?.TotalPages || Math.ceil(totalRows / rowsPerPage));

  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const paginationItems = [];
  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  const currentOrderByColumn = Object.entries({
    yarncode: 'YarnCode',
    yarnname: 'YarnName',
    yarnfactor: 'YarnFactor',
    yarndescription: 'YarnDescription'
  }).find(([backendField]) => backendField === query?.OrderBy)?.[1] || '';

  const currentOrder = query?.IsDescending === false ? 'asc' : 'desc';
  const activeFilterCount = Object.values(filters).filter((value) => String(value || '').trim() !== '').length;

  return (
    <div className="yarn-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? 'primary' : 'outline-primary'}
                  variant={showFilters ? 'solid' : 'outline'}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
            </div>
          </div>

          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3 justify-content-center yarn-filter-row">
                {columns.filter((column) => column.filterable).map((column) => (
                  <CCol key={column.id} xs={12} md={4} lg={4}>
                    <div className="filter-field">
                      <label className="filter-label">{column.label}</label>
                      <div className="filter-input-wrapper">
                        <CIcon icon={cilSearch} className="filter-input-icon" />
                        <CFormInput
                          type="text"
                          placeholder={`${t('search')} ${String(column.label || '').toLowerCase()}...`}
                          value={filters[column.id]}
                          onChange={(e) => handleFilterChange(column.id, e.target.value)}
                          className="filter-input"
                          size="sm"
                        />
                        {filters[column.id] && (
                          <CButton
                            color="link"
                            size="sm"
                            className="filter-clear-btn"
                            onClick={() => clearSingleFilter(column.id)}
                          >
                            <CIcon icon={cilX} size="sm" />
                          </CButton>
                        )}
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {column.label}
                      {column.sortable && currentOrderByColumn === column.id && (
                        <CIcon icon={currentOrder === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {tableData.length > 0 ? (
                tableData.map((item, index) => (
                  <CTableRow key={item.YarnId || index}>
                    <CTableDataCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {[item.YarnCode|| item.YarnName].filter(Boolean).join(' - ')}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>{item.YarnName}</CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="warning" shape="rounded-pill">
                        {getYarnTypeLabel(item.YarnType)}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="success" shape="rounded-pill">
                        {item.YarnFactor}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      <span title={item.YarnDescription}>
                        {item.YarnDescription?.length > 50 ? `${item.YarnDescription.substring(0, 50)}...` : item.YarnDescription}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('updateYarn')}>
                          <div
                            className="cursor-pointer p-1"
                            onClick={() => handleEdit(item)}
                            style={{ borderRadius: '4px', transition: 'background-color 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                          </div>
                        </CTooltip>
                        <CTooltip content={t('deleteYarn')}>
                          <div
                            className="cursor-pointer p-1"
                            onClick={() => handleDeleteClick(item)}
                            style={{ borderRadius: '4px', transition: 'background-color 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center">
                    {t('noDataAvailable')}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>

              <CPagination className="my-0 flex-wrap">
                <CPaginationItem aria-label={t('previous')} disabled={page === 1} onClick={() => handleChangePage(page - 1)}>
                  <span aria-hidden="true">&laquo;</span>
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem aria-label={t('next')} disabled={page === totalPages} onClick={() => handleChangePage(page + 1)}>
                  <span aria-hidden="true">&raquo;</span>
                </CPaginationItem>
              </CPagination>

              <div className="text-muted small">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteYarn')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {t('confirmDeleteYarn')}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    yarnList: state.yarnReducer?.yarnList || [],
    yarnData: state.yarnReducer?.yarnData || null,
    yarnHistorySortData: state.yarnReducer?.yarnHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteYarnRequest: (payload) => {
      dispatch(deleteYarnRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(YarnTable);
